import { useEffect, useState, useRef, useMemo } from "react";

// 3rd parties
import { Button, ButtonGroup, Col, Row } from "react-bootstrap";
import { useSelector } from "react-redux";
import styled from "styled-components";

// owner
import SntSelect from "@wrappers/ReactSelect/SntSelect";

import organizationClient from "@apis/organizationClient";
import useUser from "@hooks/useUser";
import useErrorDialog from "@/cleanup/widgets/dialog/useErrorDialog";

import SntOrgIcon from "@icons/SntOrgIcon";
import SntPartnersIcon from "@icons/SntPartnersIcon";
import SntGlobeIcon from "@icons/SntGlobeIcon";
import SntSupportUserIcon from "@icons/SntSupportUserIcon";
import { userSelector } from "@redux/selectors";
import { useCustomBranding } from "@/pages/CustomBranding/useCustomBranding";
import SntButton from "@/cleanup/widgets/forms/SntButton";
import { hasPermission } from "@/hooks/usePermissions";
import { hasFeature } from "@/hooks/useFeatures";
import { UserPermission } from "@/types/generated/UserPermission";
import { FeatureKey } from "@/types/generated/FeatureKey";
import { LanguageKey } from "@/types/generated/LanguageKey";
import { translate } from "@/hooks/useLanguage";
import type { LoginInfo } from "@/types/LoginInfo";

interface StyleCustomInfoBtnProps {
  $hoverColor?: string;
}

const StyleCustomInfoBtn = styled.div<StyleCustomInfoBtnProps>`
  button:hover {
    background-color: ${(props) => props.$hoverColor || ""};
  }
`;

interface OrganizationOption {
  label: string;
  value: number;
  type: string;
}

interface OrganizationBasicInfo {
  id: number;
  name: string;
  partnerName?: string;
  organisationType: string;
}

interface Section {
  id: string;
  title: string;
  isShow: boolean;
  icon: React.ReactNode;
}

interface OrgSelectorChangeData {
  value: number;
}

interface LanguageProps {
  [key: string]: string;
}

interface SntOrgMenuSelectorContainerProps {
  value: number | null;
  onChange?: (data: OrgSelectorChangeData) => void;
  disabled?: boolean;
  /** @deprecated Use useTranslate() internally instead */
  language?: LanguageProps;
}

const SntOrgMenuSelectorContainer = ({
  value /* orgId */,
  onChange,
  disabled = false,
}: SntOrgMenuSelectorContainerProps) => {
  const loginInfo = useSelector(userSelector) as LoginInfo;
  const { onImpersonateUser, IMPERSONATE_TYPE } = useUser();
  const { handleOpen: openErrorDialog, ErrorDialog } = useErrorDialog({});

  const { mergeDataWithValueDefault } = useCustomBranding();

  const customStyle = useMemo(() => {
    let custumInfo = {};
    if (loginInfo && loginInfo.custumInfo) {
      custumInfo = loginInfo.custumInfo;
    }
    return mergeDataWithValueDefault(custumInfo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginInfo]);

  const [sections, setSections] = useState<Section[]>([]);
  const [mode, setMode] = useState<string>();

  const [items, setItems] = useState<OrganizationOption[]>([]);

  const [orgList, setOrgList] = useState<OrganizationOption[]>([]);
  const [partnerList, setPartnerList] = useState<OrganizationOption[]>([]);
  const orgSelectedRef = useRef<number | undefined>(undefined);
  const partnerSelectedRef = useRef<number | undefined>(undefined);

  // value is updated by consume component, so, we need make sure data is loading before handling value change
  const [isLoadingData, setLoadingData] = useState(true);
  const [isFirstLoaded, setFirstLoaded] = useState(true);

  useEffect(() => {
    if (isLoadingData) return;
    if (!value) {
      setMode("ALL");
    } else {
      const find = orgList.find((data) => data.value === value);
      if (find) {
        orgSelectedRef.current = value;
        setMode("ORG");
        setItems(orgList);
      } else {
        const partnerFind = partnerList.find((data) => data.value === value);
        if (partnerFind) {
          partnerSelectedRef.current = value;
          setMode("PARTNER");
          setItems(partnerList);
        }
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, isLoadingData]);

  useEffect(() => {
    let existOrg: OrganizationOption | undefined = undefined;
    // check if value is not exist in items, need to reload data
    if (value) {
      existOrg = orgList.find((data) => data.value === value);
      if (!existOrg) {
        existOrg = partnerList.find((data) => data.value === value);
      }
      if (!existOrg) {
        setLoadingData(true);
        getOrgList();
      }
    } else if (isFirstLoaded) {
      getOrgList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const getOrgList = () => {
    organizationClient
      .getBasicInfo()
      .then(({ data }: { data: OrganizationBasicInfo[] }) => {
        let listOrg: OrganizationOption[] = [];
        const listPartner: OrganizationOption[] = [];
        data.forEach((item) => {
          const option: OrganizationOption = {
            label: item.name,
            value: item.id,
            type: item.organisationType,
          };
          if (option.type === "SYSTEM") {
            listOrg = [option, ...listOrg];
          } else if (option.type === "PARTNER") {
            option.label = item.partnerName || item.name;
            listPartner.push(option);
          } else {
            listOrg.push(option);
          }
        });

        setOrgList(listOrg);
        setPartnerList(listPartner);
        setSections([
          {
            id: "ORG",
            title: translate(LanguageKey.org_key),
            isShow: listOrg.length !== 0,
            icon: <SntOrgIcon />,
          },
          {
            id: "PARTNER",
            title: translate(LanguageKey.part_key),
            isShow: listPartner.length !== 0,
            icon: <SntPartnersIcon />,
          },
          {
            id: "ALL",
            title: translate(LanguageKey.all_key),
            isShow: true,
            icon: <SntGlobeIcon />,
          },
        ]);
        setLoadingData(false);
        setFirstLoaded(false);
      })
      .catch(() => {
        setLoadingData(false);
        setFirstLoaded(false);
      });
  };

  const selectChanged = (item: OrganizationOption) => {
    onChange?.({ value: item.value });
  };

  return (
    <StyleCustomInfoBtn $hoverColor={customStyle.adminSideBarSelectedColor}>
      <Row>
        <Col xs={12}>
          {sections && (
            <ButtonGroup
              style={{
                width: "100%",
              }}
            >
              {sections
                .filter((item) => item.isShow)
                .map((item, itemIdx) => {
                  return (
                    <SntButton
                      key={itemIdx}
                      style={{
                        background: `${
                          item.id === mode
                            ? customStyle.adminSideBarSelectedColor
                            : ""
                        }`,
                        color: customStyle.adminSideBarSectionFontColor,
                        borderColor: customStyle.adminSideBarSectionFontColor,
                        padding: "0.375rem",
                      }}
                      className={`${item.id === mode && "active"}`}
                      onClick={() => {
                        let val = 0;
                        if (item.id === "ORG") {
                          val =
                            orgSelectedRef.current ||
                            (orgList[0] ? orgList[0].value : 0);
                        } else if (item.id === "PARTNER") {
                          val =
                            partnerSelectedRef.current ||
                            (partnerList[0] ? partnerList[0].value : 0);
                        }
                        onChange?.({ value: val });
                      }}
                      icon={item.icon}
                      text={item.title}
                    />
                  );
                })}
            </ButtonGroup>
          )}
        </Col>
        {(mode === "ORG" ||
          (mode === "PARTNER" &&
            loginInfo.organisation?.organisationType !== "PARTNER")) && (
          <Col xs={12} className="mt-2">
            <div className="d-flex justify-content-between w-100">
              <div className="flex-grow-1 flex-shrink-1 text-truncate">
                <SntSelect
                  onChange={selectChanged}
                  options={items}
                  value={items.filter((option) => option.value === value)}
                  isDisabled={disabled}
                />
              </div>
              {(loginInfo.organisation?.organisationType === "SYSTEM" ||
                loginInfo.organisation?.organisationType === "PARTNER") &&
                (hasPermission(UserPermission.ORG_SUPPORT_LOGIN_VIEW_ONLY) ||
                  hasPermission(UserPermission.ORG_SUPPORT_LOGIN_ADMIN)) &&
                hasFeature(FeatureKey.impersonate) && (
                  <div className="flex-grow-0 flex-shrink-0 ms-1">
                    <Button
                      variant="sensolus-greylight"
                      onClick={() => {
                        if (value === null) return;
                        let impersonateMode = IMPERSONATE_TYPE.VIEWER;
                        if (!hasPermission(UserPermission.ORG_SUPPORT_LOGIN_VIEW_ONLY)) {
                          impersonateMode = IMPERSONATE_TYPE.ADMIN;
                        }
                        onImpersonateUser({
                          id: value,
                          type: impersonateMode,
                          onBadRequest: () => {
                            openErrorDialog(
                              translate(LanguageKey.impersonation_insufficient_rights_key)
                            );
                          },
                        });
                      }}
                      style={{
                        color: customStyle.adminSideBarSectionFontColor,
                        borderColor: customStyle.adminSideBarSectionFontColor,
                      }}
                    >
                      <SntSupportUserIcon />
                    </Button>
                  </div>
                )}
            </div>
          </Col>
        )}
      </Row>
      <ErrorDialog />
    </StyleCustomInfoBtn>
  );
};

export default SntOrgMenuSelectorContainer;
